/**
 * SearchResultItem Component
 *
 * Renders a single search result item (pair/project/arbitrage/balancer).
 * Displays symbol, project name, type badge, platform icon, archive status, and star icon.
 * Supports hover/focus states and keyboard navigation.
 */

import { StarFilled, StarOutlined } from '@ant-design/icons';
import cn from 'classnames';
import React, { useMemo } from 'react';
import type { ECexName, IDexName } from 'web3';

import ProjectListIconPairs from 'shared/components/PlatformIcon';
import { web3Configs } from 'web3';

import type { PairSearchResult } from '../types';

interface SearchResultItemProps {
  /** Search result data (currently only supports PairSearchResult for Phase 3) */
  result: PairSearchResult;

  /** Whether this item is currently focused via keyboard navigation */
  isFocused: boolean;

  /** Whether this item is in the user's watchlist */
  isFavorite?: boolean;

  /** Whether this pair is in ST zone (only for CEX pairs on mexc/kucoin/bybit) */
  isSt?: boolean;

  /** Callback when item is clicked */
  onClick: (result: PairSearchResult) => void;

  /** Callback when star icon is clicked */
  onToggleFavorite?: () => void;

  /** Optional CSS class name */
  className?: string;
}

export const SearchResultItem: React.FC<SearchResultItemProps> = ({
  result,
  isFocused,
  isFavorite = false,
  isSt = false,
  onClick,
  onToggleFavorite,
  className,
}) => {
  const handleClick = () => {
    onClick(result);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Allow Enter or Space key to select item
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(result);
    }
  };

  const handleStarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleFavorite?.();
  };

  const handleStarKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onToggleFavorite?.();
    }
  };

  const networkIcon = useMemo(() => {
    if (!result.network) return null;
    const config = web3Configs[result.network];
    if (!config?.logo) return null;
    return (
      <img src={config.logo} alt={config.label} className="h-4 w-4 rounded-full object-contain" />
    );
  }, [result.network]);

  const platformIcon = useMemo(() => {
    if (!result.platform || result.platform === 'unknown') {
      return (
        <span className="text-xs font-semibold text-slate-400">
          {result.pairType.toUpperCase()[0]}
        </span>
      );
    }

    return (
      <ProjectListIconPairs
        platform={result.platform as unknown as IDexName | ECexName}
        isIcon
        className="h-5 w-5 object-contain"
      />
    );
  }, [result.platform, result.pairType]);

  const ariaLabel = `${result.symbol}, ${result.projectName}, ${result.platform}${result.isArchived ? ', archived' : ''}${isSt ? ', ST zone' : ''}`;

  return (
    <div
      id={`result-${result.id}`}
      role="option"
      aria-selected={isFocused}
      aria-label={ariaLabel}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'flex items-center gap-3 rounded-xl px-3 py-2.5 outline-none transition-all duration-150 text-[#eef1ff] cursor-pointer focus-visible:ring-2 focus-visible:ring-indigo-500/50',
        {
          'bg-white/10 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.15)]': isFocused,
          'hover:bg-white/5': !isFocused,
          'opacity-60': result.isArchived,
        },
        className,
      )}
      tabIndex={0}
      data-testid="pair-search-result"
    >
      <div className="flex flex-1 items-center gap-3 overflow-hidden">
        <div
          aria-hidden
          className={cn(
            'relative flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-colors duration-150',
            {
              'bg-white/15': isFocused,
              'bg-white/8': !isFocused,
            },
          )}
        >
          {platformIcon}
          {networkIcon && (
            <div className="absolute -bottom-0.5 -right-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#1f1f23] ring-1 ring-white/10">
              {networkIcon}
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-[#f6f7ff]">{result.symbol}</span>
            {isSt && (
              <span className="shrink-0 rounded bg-rose-500/20 px-1.5 py-0.5 text-[9px] font-semibold text-rose-400">
                ST
              </span>
            )}
            {result.isArchived && (
              <span className="shrink-0 rounded bg-amber-500/20 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-amber-400">
                Archived
              </span>
            )}
          </div>
          <span className="truncate text-xs text-[#9ca3af]">
            {result.projectName} · {result.platform}
          </span>
        </div>
      </div>

      {/* Right content - responsive width */}
      <div className="flex w-[70px] sm:w-[100px] shrink-0 items-center justify-end gap-2 sm:gap-3">
        <span
          className={cn(
            'hidden sm:inline min-w-[44px] rounded-lg px-2 py-1 text-center text-[10px] font-semibold uppercase tracking-wide',
            {
              'bg-blue-500/20 text-blue-300': result.pairType === 'cex',
              'bg-emerald-500/20 text-emerald-300': result.pairType === 'dex',
              'bg-purple-500/20 text-purple-300': result.pairType === 'sol',
            },
          )}
        >
          {result.pairType}
        </span>

        {/* Star icon for watchlist */}
        <button
          type="button"
          tabIndex={0}
          onClick={handleStarClick}
          onKeyDown={handleStarKeyDown}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-full transition-all duration-150',
            'hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50',
            {
              'text-orange-400': isFavorite,
              'text-gray-500 hover:text-orange-300': !isFavorite,
            },
          )}
          aria-label={
            isFavorite
              ? `Remove ${result.symbol} from watchlist`
              : `Add ${result.symbol} to watchlist`
          }
          aria-pressed={isFavorite}
        >
          {isFavorite ? (
            <StarFilled className="text-sm" aria-hidden="true" />
          ) : (
            <StarOutlined className="text-sm" aria-hidden="true" />
          )}
        </button>
      </div>
    </div>
  );
};
